// src/pages/Clients/installationform.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import FormPdfPreview from './formpdfpreview';

const InstallationForm = ({ clientId, onSuccess, onClose }) => {
  // State declarations
  const [loading, setLoading] = useState(false);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [installationVideo, setInstallationVideo] = useState("");
  const [ackSubmitted, setAckSubmitted] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form fields
  const [checks, setChecks] = useState({
    installed: false,
    explained: false,
    understood: false,
    condition: false
  });
  const [comments, setComments] = useState("");
  const [clientDate, setClientDate] = useState(null);
  const [authDate, setAuthDate] = useState(null);
  
  // Signature states
  const [clientSignaturePreview, setClientSignaturePreview] = useState(null);
  const [authSignaturePreview, setAuthSignaturePreview] = useState(null);
  const [clientSignPayload, setClientSignPayload] = useState(null);
  const [authSignPayload, setAuthSignPayload] = useState(null);
  const [isClientSigned, setIsClientSigned] = useState(false);
  const [isAuthSigned, setIsAuthSigned] = useState(false);
  const [clientSignChanged, setClientSignChanged] = useState(false);
  const [authSignChanged, setAuthSignChanged] = useState(false);
  const [useDefaultAuthSign, setUseDefaultAuthSign] = useState(false);
  
  // Signature dialog states
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [activeSignType, setActiveSignType] = useState(null);
  
  // Refs
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const fileInputRef = useRef(null);
  const isDrawingRef = useRef(false);
  
  const API_BASE_URL = 'https://alcobrakeadmin.com/v3';

  const readFirst = (source, keys, fallback = null) => {
    for (const key of keys) {
      if (source?.[key] !== undefined && source?.[key] !== null) {
        return source[key];
      }
    }
    return fallback;
  };

  const toChecked = (value) => {
    if (typeof value === 'boolean') return value;
    const normalized = String(value ?? '').trim().toLowerCase();
    return ['1', 'true', 'yes', 'checked', 'on'].includes(normalized);
  };

  const normalizeSignatureSrc = (value) => {
    if (!value) return null;
    const signature = String(value).trim();

    if (!signature) return null;
    if (signature.startsWith('data:image') || signature.startsWith('blob:') || signature.startsWith('http')) {
      return signature;
    }
    if (signature.startsWith('/')) {
      return `${API_BASE_URL}${signature}`;
    }
    if (/^[A-Za-z0-9+/=]+$/.test(signature) && signature.length > 100) {
      return `data:image/png;base64,${signature}`;
    }

    return `${API_BASE_URL}/us/fullclients/${signature}`;
  };

  const getAckPayload = (data) => {
    const ackData = data?.ackdata || data?.ackData || data?.acknowledgementData || data?.acknowledgmentData;
    if (Array.isArray(ackData)) return ackData[0] || null;
    if (ackData) return ackData;
    if (Array.isArray(data)) return data[0] || null;
    return data || null;
  };

  const hasAcknowledgement = (data, ackPayload) => {
    const value = readFirst(data, ['acknowledgment', 'acknowledgement', 'ackSubmitted', 'submitted'], null);
    return toChecked(value) || !!ackPayload?.id || !!ackPayload?.clientid;
  };

  const formatDate = (date) => {
    const pad = (n) => (n < 10 ? "0" + n : n);
    return (
      pad(date.getMonth() + 1) + "/" +
      pad(date.getDate()) + "/" +
      String(date.getFullYear()).slice(-2) + " " +
      pad(date.getHours()) + ":" +
      pad(date.getMinutes()) + ":" +
      pad(date.getSeconds())
    );
  };

  const getEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("youtu.be")) {
      const id = url.split("youtu.be/")[1].split("?")[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("watch?v=")) {
      const id = url.split("watch?v=")[1].split("&")[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    return url;
  };

  const getPdf = useCallback(() => {
    if (!clientId?.id) return;
    const url = `${API_BASE_URL}/us/fullclients/getinterlockinstallation?id=${clientId.id}&v=${Date.now()}`;
    setPdfSrc(url);
  }, [clientId]);

  const getAcknowledgement = useCallback(async () => {
    if (!clientId?.id) return;
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/us/fullclients/geta200acknowledgment`,
        { params: { clientid: clientId.id } }
      );
      
      const data = response.data;
      const ackPayload = getAckPayload(data);
      const videoLinks = data?.videolinks || data?.videoLinks || ackPayload?.videolinks || ackPayload?.videoLinks;

      setInstallationVideo(videoLinks?.a200 || videoLinks?.A200 || "");
      setAckSubmitted(hasAcknowledgement(data, ackPayload));
      
      if (ackPayload) {
        const a = ackPayload;
        const clientSign = normalizeSignatureSrc(readFirst(a, ['clientsign', 'clientSign', 'client_signature', 'clientSignature']));
        const authSign = normalizeSignatureSrc(readFirst(a, ['authsign', 'authSign', 'auth_signature', 'authSignature']));

        setChecks({
          installed: toChecked(readFirst(a, ['device_handed_over_installed', 'deviceHandedOverInstalled', 'installed'])),
          explained: toChecked(readFirst(a, ['usage_explained', 'usageExplained', 'explained'])),
          understood: toChecked(readFirst(a, ['procedure_understood', 'procedureUnderstood', 'understood'])),
          condition: toChecked(readFirst(a, ['device_tested_working', 'deviceTestedWorking', 'condition']))
        });
        setComments(readFirst(a, ['comments', 'comment'], '') || "");
        setClientSignaturePreview(clientSign);
        setAuthSignaturePreview(authSign);
        setClientDate(readFirst(a, ['clientdate', 'clientDate']));
        setAuthDate(readFirst(a, ['authdate', 'authDate']));
        setClientSignPayload(null);
        setAuthSignPayload(null);
        setClientSignChanged(false);
        setAuthSignChanged(false);
        setUseDefaultAuthSign(false);
        setIsClientSigned(!!clientSign);
        setIsAuthSigned(!!authSign);
      }
    } catch (error) {
      console.error("Failed to load acknowledgement:", error);
    }
  }, [clientId]);

  const fullReset = () => {
    setClientDate(null);
    setAuthDate(null);
    setChecks({
      installed: false,
      explained: false,
      understood: false,
      condition: false
    });
    setComments("");
    setClientSignaturePreview(null);
    setAuthSignaturePreview(null);
    setClientSignPayload(null);
    setAuthSignPayload(null);
    setIsClientSigned(false);
    setIsAuthSigned(false);
    setClientSignChanged(false);
    setAuthSignChanged(false);
    setUseDefaultAuthSign(false);
    setEditMode(false);
    setAckSubmitted(false);
    
    if (pdfSrc) {
      URL.revokeObjectURL(pdfSrc);
    }
    setPdfSrc(null);
  };

  useEffect(() => {
    if (clientId?.id) {
      fullReset();
      getPdf();
      getAcknowledgement();
    }
  }, [clientId?.id, getPdf, getAcknowledgement]);

  // Canvas signature functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctxRef.current = ctx;
  };

  useEffect(() => {
    if (signDialogOpen) {
      setTimeout(() => {
        initCanvas();
      }, 100);
    }
    return () => {
      isDrawingRef.current = false;
    };
  }, [signDialogOpen]);

  const getCanvasPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * (scaleX / (window.devicePixelRatio || 1)),
      y: (clientY - rect.top) * (scaleY / (window.devicePixelRatio || 1))
    };
  };

  const startDraw = (e) => {
    isDrawingRef.current = true;
    if (e.cancelable) e.preventDefault();
    e.currentTarget?.setPointerCapture?.(e.pointerId);
    const { x, y } = getCanvasPosition(e);
    ctxRef.current?.beginPath();
    ctxRef.current?.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    if (e.cancelable) e.preventDefault();
    const { x, y } = getCanvasPosition(e);
    ctxRef.current?.lineTo(x, y);
    ctxRef.current?.stroke();
  };

  const stopDraw = (e) => {
    isDrawingRef.current = false;
    e?.currentTarget?.releasePointerCapture?.(e.pointerId);
    ctxRef.current?.beginPath();
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    ctxRef.current.clearRect(0, 0, canvas.width, canvas.height);
    
    if (activeSignType === "client") {
      setClientSignaturePreview(null);
      setClientSignPayload(null);
      setIsClientSigned(false);
    }
    if (activeSignType === "auth") {
      setAuthSignaturePreview(null);
      setAuthSignPayload(null);
      setIsAuthSigned(false);
    }
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const base64 = canvas.toDataURL("image/png");
    
    if (activeSignType === "client") {
      setClientSignaturePreview(base64);
      setClientSignChanged(true);
      setClientSignPayload(base64);
      setIsClientSigned(true);
    }
    if (activeSignType === "auth") {
      setAuthSignaturePreview(base64);
      setAuthSignChanged(true);
      setAuthSignPayload(base64);
      setIsAuthSigned(true);
    }
    
    setSignDialogOpen(false);
    setActiveSignType(null);
  };

  const openSignDialog = (type) => {
    setActiveSignType(type);
    setSignDialogOpen(true);
  };

  const cancelSignDialog = () => {
    setSignDialogOpen(false);
    setActiveSignType(null);
  };

  const triggerUpload = (type) => {
    setActiveSignType(type);
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      if (activeSignType === "client") {
        setClientSignaturePreview(e.target.result);
        setClientSignPayload(file);
        setIsClientSigned(true);
        setClientSignChanged(true);
      }
      if (activeSignType === "auth") {
        setAuthSignaturePreview(e.target.result);
        setAuthSignPayload(file);
        setIsAuthSigned(true);
        setAuthSignChanged(true);
      }
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const preloadDefaultAuthSignature = async () => {
    try {
      const response = await fetch("/rampalyam.png");
      const blob = await response.blob();
      const file = new File([blob], "authsign.png", { type: "image/png" });
      setAuthSignPayload(file);
      setAuthSignChanged(true);
      setAuthSignaturePreview(URL.createObjectURL(blob));
      setIsAuthSigned(true);
    } catch (error) {
      console.error("Failed to load default signature:", error);
    }
  };

  useEffect(() => {
    if (useDefaultAuthSign) {
      preloadDefaultAuthSignature();
    }
  }, [useDefaultAuthSign]);

  const handleDefaultAuthSignChange = (checked) => {
    setUseDefaultAuthSign(checked);

    if (!checked) {
      setAuthSignPayload(null);
      setAuthSignaturePreview(null);
      setIsAuthSigned(false);
      setAuthSignChanged(true);
    }
  };

  const base64ToFile = (base64, filename) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  const submitAcknowledgement = async () => {
    const submitTime = formatDate(new Date());
    
    let finalClientDate = clientDate;
    let finalAuthDate = authDate;
    
    if (clientSignChanged) finalClientDate = submitTime;
    if (authSignChanged) finalAuthDate = submitTime;
    
    const fd = new FormData();
    fd.append("clientdate", finalClientDate || "");
    fd.append("authdate", finalAuthDate || "");
    fd.append("clientid", clientId.id);
    fd.append("device_handed_over_installed", checks.installed ? 1 : 0);
    fd.append("usage_explained", checks.explained ? 1 : 0);
    fd.append("procedure_understood", checks.understood ? 1 : 0);
    fd.append("device_tested_working", checks.condition ? 1 : 0);
    fd.append("comments", comments);
    
    if (clientSignPayload instanceof File) {
      fd.append("clientsign", clientSignPayload);
    } else if (clientSignPayload) {
      fd.append("clientsign", base64ToFile(clientSignPayload, "clientsign.png"));
    }
    
    if (authSignPayload instanceof File) {
      fd.append("authsign", authSignPayload);
    } else if (authSignPayload) {
      fd.append("authsign", base64ToFile(authSignPayload, "authsign.png"));
    }
    
    try {
      setLoading(true);
      await axios.post(
        `${API_BASE_URL}/us/fullclients/updateinterlockacknowledgment`,
        fd
      );
      
      setClientSignChanged(false);
      setAuthSignChanged(false);
      setAckSubmitted(true);
      setEditMode(false);
      
      getPdf();
      getAcknowledgement();
      
      if (onSuccess) onSuccess();
      
      alert("Acknowledgement submitted successfully");
    } catch (error) {
      console.error("Submit failed:", error);
      alert("Failed to submit: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadForm = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/us/fullclients/getinterlockinstallation?id=${clientId.id}`
      );
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${clientId.sno || clientId.id}-installation-form.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download form");
    }
  };

  const canSubmit = Object.values(checks).some(v => v === true) || isClientSigned || isAuthSigned;
  const isReadOnly = ackSubmitted && !editMode;

  return (
    <div className="installation-form-container">
      {/* Top Action Bar */}
      <div className="action-bar">
        <div className="font-weight-bold">Installation Form</div>
        <div className="action-buttons">
          <button 
            className="btn-secondary" 
            onClick={onClose}
          >
            Close
          </button>
          <button className="btn-secondary" onClick={downloadForm}>
            Download Form
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
      </div>

      {/* Main Content */}
      <div className="content-wrapper">
        {/* Left: PDF pages */}
            <div className="pdf-section">
            {pdfSrc ? (
                <FormPdfPreview src={pdfSrc} title="Installation Form" />
            ) : (
                <div className="pdf-loading">
                <div className="loader"></div>
                <span>Loading PDF...</span>
                </div>
            )}
            </div>

        {/* Right: Video + Acknowledgement */}
        <div className="right-section">
          {/* Video */}
          <div className="video-box">
            {installationVideo ? (
              <iframe
                src={getEmbedUrl(installationVideo)}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Installation Video"
              />
            ) : (
              <div className="video-placeholder">Video not available</div>
            )}
          </div>

          {/* Acknowledgement Section */}
          <div className="ack-content">
            {isReadOnly ? (
              <>
                <div className="alert-success">Acknowledgement already submitted</div>
                <button className="btn-primary mt-3" onClick={() => setEditMode(true)}>
                  Edit Acknowledgement
                </button>
              </>
            ) : (
              <>
                <h3>Ignition Interlock Device Acknowledgement</h3>
                <p>I hereby acknowledge the following:</p>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={checks.installed}
                    disabled={isReadOnly}
                    onChange={(e) => setChecks({ ...checks, installed: e.target.checked })}
                  />
                  Ignition Interlock Device has been installed in my vehicle.
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={checks.explained}
                    disabled={isReadOnly}
                    onChange={(e) => setChecks({ ...checks, explained: e.target.checked })}
                  />
                  Usage and safety instructions have been explained.
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={checks.understood}
                    disabled={isReadOnly}
                    onChange={(e) => setChecks({ ...checks, understood: e.target.checked })}
                  />
                  I understand how to operate the device.
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={checks.condition}
                    disabled={isReadOnly}
                    onChange={(e) => setChecks({ ...checks, condition: e.target.checked })}
                  />
                  Vehicle is in good working condition.
                </label>

                <textarea
                  className="comments-input"
                  placeholder="Comments *"
                  rows="3"
                  maxLength="235"
                  value={comments}
                  disabled={isReadOnly}
                  onChange={(e) => setComments(e.target.value)}
                />
                <div className="char-counter">{comments.length}/235</div>

                {/* Client Signature */}
                <h4>Client Signature</h4>
                <div className="signature-buttons">
                  <button className="btn-success" onClick={() => openSignDialog("client")} disabled={isReadOnly}>
                    Sign
                  </button>
                  <button className="btn-info" onClick={() => triggerUpload("client")} disabled={isReadOnly}>
                    Upload
                  </button>
                </div>
                <div className="helper-text">
                  Please upload a PNG file under 50 KB. Make sure the signature background is removed before uploading.
                </div>
                {clientSignaturePreview && (
                  <div className="signature-preview">
                    <img src={clientSignaturePreview} alt="Client signature" />
                  </div>
                )}

                {/* Authorized Signature */}
                <h4 className="mt-4">Authorized Signature</h4>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={useDefaultAuthSign}
                    disabled={isReadOnly}
                    onChange={(e) => handleDefaultAuthSignChange(e.target.checked)}
                  />
                  Use default authorizer signature
                </label>

                <div className="signature-buttons">
                  <button
                    className="btn-success"
                    onClick={() => openSignDialog("auth")}
                    disabled={isReadOnly || useDefaultAuthSign}
                  >
                    Sign
                  </button>
                  <button
                    className="btn-info"
                    onClick={() => triggerUpload("auth")}
                    disabled={isReadOnly || useDefaultAuthSign}
                  >
                    Upload
                  </button>
                </div>
                <div className="helper-text">
                  Please upload a PNG file under 50 KB. Make sure the signature background is removed before uploading.
                </div>
                {authSignaturePreview && (
                  <div className="signature-preview">
                    <img src={authSignaturePreview} alt="Authorized signature" />
                  </div>
                )}

                {!isReadOnly && (
                <div className="mt-3">
                  <button 
                    className="btn-primary" 
                    onClick={submitAcknowledgement}
                    disabled={loading || !canSubmit}
                  >
                    {loading ? 'Saving...' : 'Save Acknowledgement'}
                  </button>
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Signature Dialog */}
      {signDialogOpen && (
        <div className="modal-overlay" onClick={cancelSignDialog}>
          <div className="sign-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sign-dialog-header">
              <span>{activeSignType === "client" ? "Client Signature" : "Authorized Signature"}</span>
              <button className="close-btn" onClick={cancelSignDialog}>✕</button>
            </div>
            <div className="sign-dialog-body">
              <canvas
                ref={canvasRef}
                className="sign-canvas"
                onPointerDown={startDraw}
                onPointerMove={draw}
                onPointerUp={stopDraw}
                onPointerCancel={stopDraw}
                onPointerLeave={stopDraw}
                style={{ touchAction: 'none' }}
              ></canvas>
            </div>
            <div className="sign-dialog-footer">
              <button className="btn-secondary" onClick={clearSignature}>Clear</button>
              <button className="btn-success" onClick={saveSignature}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallationForm;
